import { ColumnDef } from '@tanstack/react-table';
import { Download, Edit, Eye, MoreHorizontal, Plus } from 'lucide-react';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../components/Header';
import { GenerateCodesDialog } from '../components/qr/GenerateCodesDialog';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { DataTable } from '../components/ui/data-table';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../components/ui/dropdown-menu';
import { MessageDialog } from '../components/ui/message-dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { useDebouncedValue } from '../hooks/useDebouncedValue';
import { displayQrCodeStatusLabel, qrStatusBadgeClassName } from '../utils/qrCodeStatusLabel';

interface User {
  id: string;
  email: string;
  first_name: string;
  last_name: string;
  roles: Array<{ key: string; name: string; description: string }>;
}

interface QRCodeData {
  id: string;
  code: string;
  status: string;
  asset_instance_id: string | null;
  asset_instance?: {
    id: string;
    instance_number: string;
    template: {
      name: string;
      images?: Array<{
        id: string;
        url: string;
        display_order: number;
      }>;
    };
  };
  scan_count: number;
  generated_at: string;
  assigned_at: string | null;
  last_scanned_at: string | null;
}

interface QRCodeStats {
  total: number;
  assigned: number;
  unassigned: number;
  assignedPercentage: number;
  totalScans: number;
  avgScans: string;
  byStatus: {
    generated: number;
    printed: number;
    assigned: number;
    active: number;
    damaged: number;
  };
}

const QRCodes: React.FC = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState<User | null>(null);
  const [codes, setCodes] = useState<QRCodeData[]>([]);
  const [stats, setStats] = useState<QRCodeStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [generateDialogOpen, setGenerateDialogOpen] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [errorDialogOpen, setErrorDialogOpen] = useState(false);

  // Filter states
  const [assignmentFilter, setAssignmentFilter] = useState<string>('all');
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [pages, setPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebouncedValue(search, 300);

  useEffect(() => {
    const userData = localStorage.getItem('user');
    if (userData) {
      setUser(JSON.parse(userData));
    }
  }, []);

  const loadCodes = useCallback(async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');

      // Build query parameters
      const params = new URLSearchParams({
        page: String(page),
        limit: String(limit),
      });

      // Add assignment filter if not 'all'
      if (assignmentFilter !== 'all') {
        params.append('assigned', assignmentFilter === 'assigned' ? 'true' : 'false');
      }
      if (debouncedSearch) params.append('search', debouncedSearch);

      const response = await fetch(`/api/qr-codes?${params.toString()}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      const data = await response.json();

      if (data.success) {
        setCodes(data.data);
        if (data.pagination) {
          setTotal(data.pagination.total || data.pagination.totalItems || 0);
          setPages(data.pagination.totalPages || data.pagination.pages || 1);
        }
      } else {
        setErrorMessage(data.message || 'Failed to load QR codes');
        setErrorDialogOpen(true);
      }
    } catch (error) {
      console.error('Error loading QR codes:', error);
      setErrorMessage('Failed to load QR codes');
      setErrorDialogOpen(true);
    } finally {
      setLoading(false);
    }
  }, [assignmentFilter, page, limit, debouncedSearch]);

  const loadStats = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');

      const response = await fetch('/api/qr-codes/stats', {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      const data = await response.json();

      if (data.success) {
        setStats(data.data);
      }
    } catch (error) {
      console.error('Error loading QR code stats:', error);
    }
  }, []);

  useEffect(() => {
    loadCodes();
    loadStats();
  }, [loadCodes, loadStats]);

  const handleGenerateSuccess = () => {
    setGenerateDialogOpen(false);
    loadCodes();
    loadStats();
  };

  const handleExportCSV = async () => {
    try {
      setExporting(true);
      const token = localStorage.getItem('token');

      const response = await fetch('/api/qr-codes/export-csv', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          codeIds: undefined, // Export all codes
        }),
      });

      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `qr-codes-${Date.now()}.csv`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
      } else {
        setErrorMessage('Failed to export CSV');
        setErrorDialogOpen(true);
      }
    } catch (error) {
      console.error('Error exporting CSV:', error);
      setErrorMessage('Failed to export CSV');
      setErrorDialogOpen(true);
    } finally {
      setExporting(false);
    }
  };

  const columns: ColumnDef<QRCodeData>[] = useMemo(() => [
    {
      accessorKey: 'code',
      header: 'Code',
      cell: ({ row }) => (
        <button
          onClick={() => navigate(`/qr-codes/${row.original.id}`)}
          className="font-mono font-medium text-primary hover:underline"
        >
          {row.original.code}
        </button>
      ),
    },
    {
      accessorKey: 'status',
      header: 'Status',
      cell: ({ row }) => {
        const label = displayQrCodeStatusLabel(row.original);
        return (
          <Badge className={qrStatusBadgeClassName[label] ?? 'bg-gray-500'}>
            {label}
          </Badge>
        );
      },
    },
    {
      id: 'product_image',
      header: 'Product',
      cell: ({ row }) => {
        const asset = row.original.asset_instance;
        if (!asset) return <span className="text-muted-foreground">-</span>;

        const imageUrl = asset.template?.images?.[0]?.url;

        return (
          <div className="flex items-center gap-3">
            <div className="h-16 w-16 flex-shrink-0 bg-muted rounded overflow-hidden">
              {imageUrl ? (
                <img
                  src={imageUrl}
                  alt={asset.template?.name || 'Product'}
                  className="h-full w-full object-cover"
                />
              ) : (
                <div className="h-full w-full flex items-center justify-center text-muted-foreground text-xs">
                  No image
                </div>
              )}
            </div>
            <button
              onClick={() => navigate(`/units/${asset.id}`)}
              className="text-primary hover:underline"
            >
              {asset.template?.name || 'Unknown'}
            </button>
          </div>
        );
      },
    },
    {
      id: 'assigned_unit',
      header: 'Unit',
      cell: ({ row }) => {
        const asset = row.original.asset_instance;
        if (!asset) return <span className="text-muted-foreground">-</span>;
        return (
          <span className="text-sm text-muted-foreground">
            {asset.instance_number}
          </span>
        );
      },
    },
    {
      accessorKey: 'scan_count',
      header: 'Scans',
      cell: ({ row }) => (
        <div className="text-center">{row.original.scan_count}</div>
      ),
    },
    {
      accessorKey: 'generated_at',
      header: 'Generated',
      cell: ({ row }) => {
        const date = new Date(row.original.generated_at);
        const today = new Date().toDateString();

        const timeStr = date.toLocaleTimeString(undefined, {
          hour: '2-digit',
          minute: '2-digit',
          hour12: true
        });

        if (date.toDateString() === today) {
          return <div className="text-sm">Today {timeStr}</div>;
        }

        const dateStr = date.toLocaleDateString(undefined, {
          day: '2-digit',
          month: '2-digit',
          year: 'numeric'
        });

        return (
          <div className="text-sm">
            <div>{dateStr}</div>
            <div className="text-xs text-muted-foreground">{timeStr}</div>
          </div>
        );
      },
    },
    {
      id: 'actions',
      header: 'Actions',
      cell: ({ row }) => (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" className="h-8 w-8 p-0">
              <MoreHorizontal className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem onClick={() => navigate(`/qr-codes/${row.original.id}`)}>
              <Eye className="h-4 w-4 mr-2" />
              View Details
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => navigate(`/qr-codes/${row.original.id}/assign`)}>
              <Edit className="h-4 w-4 mr-2" />
              {row.original.asset_instance_id ? 'Reassign' : 'Assign'}
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      ),
    },
  ], [navigate]);

  return (
    <div className="min-h-screen bg-background">
      <Header user={user} currentPage="qr-codes" />

      <main className="container mx-auto px-4 py-8">
        <div className="flex justify-between items-center mb-6">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">
              QR Codes
            </h1>
          </div>
          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={handleExportCSV}
              disabled={exporting}
            >
              <Download className="h-4 w-4 mr-2" />
              {exporting ? 'Exporting...' : 'Export CSV'}
            </Button>
            <Button onClick={() => setGenerateDialogOpen(true)}>
              <Plus className="h-4 w-4 mr-2" />
              Generate Codes
            </Button>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <div
            onClick={() => setAssignmentFilter('all')}
            className={`p-6 rounded-lg shadow-sm cursor-pointer transition-all border border-transparent hover:border-primary ${
              assignmentFilter === 'all' ? 'bg-primary/5' : 'bg-card'
            }`}
          >
            <div className="text-2xl font-bold text-primary">
              {stats ? stats.total : '-'}
            </div>
            <div className="text-sm text-muted-foreground">Total QR Codes</div>
          </div>

          <div
            onClick={() => setAssignmentFilter('assigned')}
            className={`p-6 rounded-lg shadow-sm cursor-pointer transition-all border border-transparent hover:border-primary ${
              assignmentFilter === 'assigned' ? 'bg-primary/5' : 'bg-card'
            }`}
          >
            <div className="text-2xl font-bold text-green-600">
              {stats ? stats.assigned : '-'}
            </div>
            <div className="text-sm text-muted-foreground">
              Assigned ({stats ? stats.assignedPercentage : 0}%)
            </div>
          </div>

          <div
            onClick={() => setAssignmentFilter('unassigned')}
            className={`p-6 rounded-lg shadow-sm cursor-pointer transition-all border border-transparent hover:border-primary ${
              assignmentFilter === 'unassigned' ? 'bg-primary/5' : 'bg-card'
            }`}
          >
            <div className="text-2xl font-bold text-blue-600">
              {stats ? stats.unassigned : '-'}
            </div>
            <div className="text-sm text-muted-foreground">Unassigned</div>
          </div>

          <div className="bg-card p-6 rounded-lg shadow-sm">
            <div className="text-2xl font-bold text-purple-600">
              {stats ? stats.totalScans : '-'}
            </div>
            <div className="text-sm text-muted-foreground">
              Total Scans (avg {stats ? stats.avgScans : '0'})
            </div>
          </div>
        </div>

        <DataTable
          columns={columns}
          data={codes}
          loading={loading}
          searchKey="code"
          searchPlaceholder="Search by code..."
          searchValue={search}
          onSearchChange={(v) => { setSearch(v); setPage(1); }}
          emptyMessage="No QR codes found"
          serverSide={true}
          pagination={{ total, page, limit, pages }}
          onPaginationChange={(newPage, newLimit) => {
            if (newLimit !== limit) { setLimit(newLimit); setPage(1); } else { setPage(newPage); }
          }}
          toolbarContentLeft={
            <Select
              value={assignmentFilter}
              onValueChange={(value) => setAssignmentFilter(value)}
            >
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="Filter by assignment" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All QR Codes</SelectItem>
                <SelectItem value="assigned">Assigned</SelectItem>
                <SelectItem value="unassigned">Unassigned</SelectItem>
              </SelectContent>
            </Select>
          }
        />
      </main>

      <GenerateCodesDialog
        open={generateDialogOpen}
        onOpenChange={setGenerateDialogOpen}
        onSuccess={handleGenerateSuccess}
      />

      <MessageDialog
        open={errorDialogOpen}
        onOpenChange={setErrorDialogOpen}
        title="Error"
        description={errorMessage}
        variant="destructive"
      />
    </div>
  );
};

export default QRCodes;
